import type WhiteboardController from '../../controllers/WhiteboardController';
import type { ObjectMenuSection } from './ObjectMenu';
import { flattenSections, iconTitleize } from '../../utils/objectMenu';
import { type SpriteSvgItem } from '../../types';
import { uniq } from 'lodash';
import type ShapeSpriteIcon from '../../shapes/ShapeSpriteIcon';
import type Shape from '../../objects/Shape';
import { type IconTabItem } from './IconsTab';
import { capitalCase } from '../../utils/string';

import stampThumbsUp from '../../assets/icons/stampThumbsUp.svg';
import stampThumbsDown from '../../assets/icons/stampThumbsDown.svg';
import stampQuestion from '../../assets/icons/stampQuestion.svg';
import stampStar from '../../assets/icons/stampStar.svg';
import stampSlightSmile from '../../assets/icons/stampSlightSmile.svg';
import stampWorried from '../../assets/icons/stampWorried.svg';
import stampSmile from '../../assets/icons/stampSmile.svg';
import stampDisappointed from '../../assets/icons/stampDisappointed.svg';
import stampHeartEyes from '../../assets/icons/stampHeartEyes.svg';
import stampConfused from '../../assets/icons/stampConfused.svg';
import stampStarStruck from '../../assets/icons/stampStarStruck.svg';
import stampAstonished from '../../assets/icons/stampAstonished.svg';
import stampSlightSmileElle from '../../assets/icons/stampSlightSmileElle.svg';
import stampWorriedElle from '../../assets/icons/stampWorriedElle.svg';
import stampSmileElle from '../../assets/icons/stampSmileElle.svg';
import stampDisappointedElle from '../../assets/icons/stampDisappointedElle.svg';
import stampHeartEyesElle from '../../assets/icons/stampHeartEyesElle.svg';
import stampConfusedElle from '../../assets/icons/stampConfusedElle.svg';
import stampStarStruckElle from '../../assets/icons/stampStarStruckElle.svg';
import stampAstonishedElle from '../../assets/icons/stampAstonishedElle.svg';

function ahaSvgIconString(spriteName) {
  switch (spriteName) {
    case 'aha-icon--thumbs-down':
      return stampThumbsDown;
    case 'aha-icon--thumbs-up':
      return stampThumbsUp;
    case 'aha-icon--question':
      return stampQuestion;
    case 'aha-icon--star':
      return stampStar;
    case 'aha-icon--slight-smile':
      return stampSlightSmile;
    case 'aha-icon--frowning':
      return stampWorried;
    case 'aha-icon--smile':
      return stampSmile;
    case 'aha-icon--disappointed':
      return stampDisappointed;
    case 'aha-icon--heart-eyes':
      return stampHeartEyes;
    case 'aha-icon--confused':
      return stampConfused;
    case 'aha-icon--star-struck':
      return stampStarStruck;
    case 'aha-icon--astonished':
      return stampAstonished;
    case 'aha-icon--slight-smile-elle':
      return stampSlightSmileElle;
    case 'aha-icon--frowning-elle':
      return stampWorriedElle;
    case 'aha-icon--smile-elle':
      return stampSmileElle;
    case 'aha-icon--disappointed-elle':
      return stampDisappointedElle;
    case 'aha-icon--heart-eyes-elle':
      return stampHeartEyesElle;
    case 'aha-icon--confused-elle':
      return stampConfusedElle;
    case 'aha-icon--star-struck-elle':
      return stampStarStruckElle;
    case 'aha-icon--astonished-elle':
      return stampAstonishedElle;
    default:
      throw new Error(`Invalid aha icon: ${spriteName}`);
  }
}

// Slash is not valid in css id, so we use hyphens to delimit the svg ids
const PATH_DELIMITER = '-';

interface SpriteSheetData {
  id: string;
  data: string;
}

// Singleton class for loading sprite sheets, parsing the icons, and extracting
// relevant properties for a given sprite (e.g., name, view component, etc.)
export class SpriteSvg {
  static instance: SpriteSvg;

  spriteSvgs: Record<string, string> = {};
  // Allows access to the structure asynchronously.
  structure: Promise<ObjectMenuSection<SpriteSvgItem>[]>;
  controller: WhiteboardController;

  private resolveStructurePromise: (
    value: ObjectMenuSection<SpriteSvgItem>[]
  ) => void;

  private constructor() {
    // Initialize the structurePromise with a placeholder promise
    this.structure = new Promise(resolve => {
      this.resolveStructurePromise = resolve;
    });
  }

  static getInstance(): SpriteSvg {
    if (!this.instance) {
      this.instance = new SpriteSvg();
    }
    return this.instance;
  }

  async getStructure(): Promise<ObjectMenuSection<SpriteSvgItem>[]> {
    return await this.structure;
  }

  async loadSpriteSheets(sheets: SpriteSheetData[]) {
    const structure: ObjectMenuSection<SpriteSvgItem>[] = [];
    const promises = sheets.map(async sheet => {
      const domParser = new DOMParser();
      let { data } = sheet;

      let doc;
      // Append the svgs to the document. Hopefully a temp workaround for use tags,
      // which have stricter CORS policies.
      let div = document.getElementById(sheet.id);
      if (!div) {
        if (data.startsWith('http')) {
          // The sprite sheet is actually a URL to the SVG file
          data = await (await fetch(data)).text();
        }

        doc = domParser.parseFromString(data, 'image/svg+xml');

        div = document.createElement('div');
        div.setAttribute('id', sheet.id);
        div.innerHTML = data;
        // The sprite sheet has to be visible (e.g., not display: none) in order
        // for styles to be accessible from the shadow dom
        div.style.position = 'absolute';
        div.style.zIndex = '-1';
        div.style.top = '-1000px';
        div.style.opacity = '0';
        document.body.appendChild(div);
      } else {
        doc = domParser.parseFromString(div.innerHTML, 'image/svg+xml');
      }
      return structure.push(
        ...(await this.generateMenuStructure(sheet.id, doc))
      );
    });
    await Promise.all(promises);
    // Alphabetical sort also gives the icons in market share order: AWS, Azure, GCP
    structure.sort((a, b) => a.title.localeCompare(b.title));
    this.resolveStructurePromise(structure);
  }

  async generateMenuStructure(
    sheetId: string,
    svgRoot: Document
  ): Promise<ObjectMenuSection<SpriteSvgItem>[]> {
    const menuSection: ObjectMenuSection<SpriteSvgItem>[] = [];
    try {
      const gTags = svgRoot.querySelectorAll('g[spriteId]');
      gTags.forEach(tag => {
        const parts = tag.id.split(PATH_DELIMITER);
        if (parts.length <= 1) {
          console.error('Invalid icon id');
          return;
        }
        let section = menuSection.find(s => s.title === parts[0]);
        if (!section) {
          section = this.createSection(parts[0]);
          menuSection.push(section);
        }

        this.addSection(
          parts.slice(1),
          section,
          `${sheetId}${PATH_DELIMITER}${parts[0]}`,
          tag.querySelector('svg')?.getAttribute('viewBox') ?? ''
        );
      });
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }

    return menuSection;
  }

  addSection(
    parts: string[],
    section: ObjectMenuSection<SpriteSvgItem>,
    path: string,
    viewBox: string
  ): void {
    if (parts.length === 1) {
      section.items.push(this.createItem(parts[0], path, viewBox));
      return;
    }

    let subSection = section.sections.find(s => s.title === parts[0]);
    if (!subSection) {
      subSection = this.createSection(parts[0]);
      section.sections.push(subSection);
    }

    this.addSection(
      parts.slice(1),
      subSection,
      `${path}${PATH_DELIMITER}${parts[0]}`,
      viewBox
    );
  }

  createSection(title: string) {
    return {
      title,
      items: [],
      sections: [],
    };
  }

  createItem(name: string, path: string, viewBox: string) {
    return {
      path: `${path}${PATH_DELIMITER}${name}`,
      viewBox,
    };
  }

  sheetNameFromPath(spritePath: string) {
    return spritePath.split(PATH_DELIMITER)[0];
  }

  serviceFromPath(spritePath: string) {
    return spritePath.split(PATH_DELIMITER)[1];
  }

  spriteId(spritePath: string): string {
    return spritePath.split(PATH_DELIMITER).slice(1).join(PATH_DELIMITER);
  }

  displayName(spritePath: string) {
    const split = spritePath.split(PATH_DELIMITER);
    let name = split[split.length - 1];
    // Google cloud icons are all lowercase, and this is the easiest point at
    // which to detect the service provider
    if (this.serviceFromPath(spritePath).toLowerCase() === 'google_cloud') {
      name = capitalCase(name);
    }
    return iconTitleize(name.replace(/[-_]/g, ' '));
  }

  // Returns the svg for this icon as a string, used for generating an image in the canvas
  svgStringForIcon(spritePath: string) {
    if (spritePath.startsWith('aha-icon')) {
      return ahaSvgIconString(spritePath);
    }

    const sheetName = this.sheetNameFromPath(spritePath);
    const id = this.spriteId(spritePath);
    let svgString = document
      .getElementById(sheetName)
      ?.querySelector(`#${id}`)?.innerHTML;

    // Manually inject the xmlns attribute if it's missing. When creating the
    // sprite sheet, Nokogiri only allows namespacing for the parent element,
    // which makes sense. However, in order to have a valid svg, we need the
    // namespace on the child svg element as well.
    if (!svgString?.includes('<svg xmlns="http://www.w3.org/2000/svg')) {
      svgString = svgString?.replace(
        /<svg/,
        '<svg xmlns="http://www.w3.org/2000/svg"'
      );
    }
    return svgString;
  }

  svgRef(spritePath: string) {
    const id = this.spriteId(spritePath);
    return `#${id}`;
  }

  async iconSetForItem(spritePath: string | undefined): Promise<IconTabItem[]> {
    if (spritePath) {
      const service = this.serviceFromPath(spritePath);
      const structure = await this.getStructure();
      const section = structure.find(s => s.title === service);
      if (section) {
        const items = uniq(flattenSections(section.sections));
        return items;
      }
    }
    return [];
  }

  async iconDataForObject(object: Shape<ShapeSpriteIcon>) {
    const service = this.serviceFromPath(object.iconName!);
    const structure = await this.getStructure();
    const section = structure.find(s => s.title === service);
    if (section) {
      const items = uniq(flattenSections(section.sections));
      return items.find(i => i.path === object.iconName);
    }
  }
}

export { ahaSvgIconString };
